import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import {
  setModalUiContent,
  setPostDataAttribute,
  updateIGProductTagModalCloseState,
  updateIGTagModalCloseState,
  updateIGTags,
  updateProductCatalog,
  updateProductIGTags
} from '../../../../../redux/slices/lomavisCreator/lomavisCreator';
import TagComponent from './tagComponent';
import { useTranslate } from '@tolgee/react';
import './tagger.css';
import {
  IMAGE,
  MAX_IMAGE_PRODUCT_TAGS,
  MAX_POST_PRODUCT_TAGS,
  VIDEO
} from '../../../config/constants';
import { useTimeout } from 'react-use';
import CreatorTitle from '../../commonElements/creatorTitle';
import { INSTAGRAM } from '../../../../../config/constants';
import { TypeaheadProductSelection } from './productSelectionInput';
import { Catalog, ProductCatalogSelection } from './productCatalogSelect';
import { lomavisShowToastr } from '@/utils/ui/toastr';
import { debounce } from 'lodash';
import { ProductCard } from './sharedComponents';

interface Props {
  selectedFile: any;
  croppedImageUrl: string;
  crop: any;
  saveBtnRef: React.MutableRefObject<any>;
  handleClose: Function;
  setStartSubmission: React.Dispatch<React.SetStateAction<boolean>>;
  startSubmission: boolean;
  setSelectedFile: React.Dispatch<any>;
  instagramUserTags: any;
  currentFileIndex: number;
  editorChanged: boolean;
  setEditorChanged: React.Dispatch<React.SetStateAction<boolean>>;
  submitRef: any;
  IGMedia: any;
  selected_instagram_catalog: Catalog | null;
}
export interface Tag {
  x: number;
  y: number;
  x_percentage: number;
  y_percentage: number;
  productInfo: any;
}
const Tagger: React.FC<Props> = ({
  selectedFile,
  croppedImageUrl,
  crop,
  saveBtnRef,
  handleClose,
  setStartSubmission,
  startSubmission,
  setSelectedFile,
  currentFileIndex,
  instagramUserTags,
  selected_instagram_catalog,
  editorChanged,
  setEditorChanged,
  IGMedia,
  submitRef
}) => {
  const { t } = useTranslate();
  const dispatch = useDispatch();
  const isMultiImage = IGMedia.length > 1;
  const [tags, setTags] = useState<Tag[]>(
    instagramUserTags[selectedFile?.uuid] || []
  );

  const [currentTag, setCurrentTag] = useState<Tag | null>(null);
  const [editingTagIndex, setEditingTagIndex] = useState<number | null>(null);
  const [containerHeight, setContainerHeight] = useState('500px');

  const [imageLoaded, setImageLoaded] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const [editTag, setEditTag] = useState(null);

  const [selectedCatalog, setSelectedCatalog] = useState<Catalog | null>(
    selected_instagram_catalog
  );
  const [emptyCatalog, setEmptyCatalog] = useState(false);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef(null);
  const tagRef = useRef(null);

  const {
    postData: {
      [INSTAGRAM]: { additional_publication_data = {}, media = [] }
    }
  } = useSelector((state: any) => state.lomavisCreator);

  const { instagram_shopping } = useSelector(
    (state: any) => state.lomavisCreator.lomavisCreatorConfig
  );

  const totalTagCount = Object.values(instagramUserTags).reduce(
    (total: number, tagList) => {
      if (Array.isArray(tagList)) {
        return total + tagList?.length;
      }
      return total;
    },
    0
  );
  const maxPerImage = isMultiImage
    ? MAX_IMAGE_PRODUCT_TAGS
    : MAX_POST_PRODUCT_TAGS;
  const maxTotal = MAX_POST_PRODUCT_TAGS;

  const handleImageClick = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (tags.length >= maxPerImage) {
      lomavisShowToastr(
        `${t(
          'lomavisCreator.additionalOptions.productMaxTags1'
        )} ${maxPerImage} ${t(
          'lomavisCreator.additionalOptions.productMaxTags2'
        )}`,
        'error'
      );
      // toast.error(`You can only tag up to ${maxPerImage} products per image.`);
      return;
    }

    if (totalTagCount >= maxTotal) {
      lomavisShowToastr(
        t('lomavisCreator.additionalOptions.allProductMaxTags'),
        'error'
      );

      return;
    }

    if (!editorChanged) {
      dispatch(
        updateIGProductTagModalCloseState({
          state: true
        })
      );

      dispatch(
        setModalUiContent({
          modal: 'IGProductTags',
          attribute: 'allowClose',
          value: false
        })
      );

      setEditorChanged(true);
    }

    if (imageRef.current) {
      const rect = imageRef.current.getBoundingClientRect();
      const x = Math.round(e.clientX - rect.left);
      const y = Math.round(e.clientY - rect.top);

      // Get the natural dimensions of the image
      const naturalWidth = imageRef.current.naturalWidth;
      const naturalHeight = imageRef.current.naturalHeight;

      // Get the current displayed dimensions of the image
      const currentWidth = rect.width;
      const currentHeight = rect.height;

      // Calculate scaling factors
      const scaleX = naturalWidth / currentWidth;
      const scaleY = naturalHeight / currentHeight;

      // Calculate the absolute position
      const absoluteX = Math.round(x * scaleX);
      const absoluteY = Math.round(y * scaleY);
      const x_percentage = getPercentageX(x);
      const y_percentage = getPercentageY(y);

      setCurrentTag({
        x: absoluteX,
        y: absoluteY,
        x_percentage: x_percentage,
        y_percentage: y_percentage,
        productInfo: null
      });
      setEditingTagIndex(tags.length);
    }
  };
  const getRelativeX = (x) => {
    const rect = imageRef.current?.getBoundingClientRect();

    const currentWidth = rect?.width;

    return x * currentWidth;
  };
  const getRelativeY = (y) => {
    const rect = imageRef.current?.getBoundingClientRect();

    const currentHeight = rect?.height;

    return y * currentHeight;
  };

  const getPercentageX = (x) => {
    const rect = imageRef.current?.getBoundingClientRect();
    const currentWidth = rect?.width;

    return x / currentWidth;
  };
  const getPercentageY = (y) => {
    const rect = imageRef.current?.getBoundingClientRect();

    const currentHeight = rect?.height;

    return y / currentHeight;
  };

  const isProductDuplicate = (
    productId: string,
    currentUUID: string,
    currentTags: any[],
    allInstagramTags: Record<string, any[]>
  ): boolean => {
    const allOtherTags = Object.entries(allInstagramTags)
      .filter(([uuid]) => uuid !== currentUUID)
      .flatMap(([_, tagArray]) => tagArray || []);

    const isDuplicateAcrossAll = allOtherTags.some(
      (tag) => tag?.productInfo?.product_id === productId
    );

    const isDuplicateInCurrent = currentTags.some(
      (tag) => tag?.productInfo?.product_id === productId
    );

    return isDuplicateAcrossAll || isDuplicateInCurrent;
  };

  const handleInputChange = (info) => {
    if (!info) return;

    const isDuplicate = isProductDuplicate(
      info.product_id,
      selectedFile?.uuid,
      tags,
      instagramUserTags
    );

    if (isDuplicate) {
      lomavisShowToastr(
        t('lomavisCreator.additionalOptions.duplicateProductInAll'),
        'error'
      );
      return;
    }

    if (currentTag) {
      setCurrentTag({ ...currentTag, productInfo: info });
    }

    setIsFocused(false);

    if (currentTag && info && tags?.length < 10) {
      const updatedTags = [...tags];
      if (editingTagIndex !== null && editingTagIndex < tags.length) {
        updatedTags[editingTagIndex] = { ...currentTag, productInfo: info };
      } else {
        updatedTags.push({ ...currentTag, productInfo: info });
      }

      setTags(updatedTags);
      setCurrentTag(null);
      setEditingTagIndex(null);
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);

    if (currentTag && currentTag.productInfo && tags?.length < 10) {
      const updatedTags = [...tags];
      if (editingTagIndex !== null && editingTagIndex < tags.length) {
        updatedTags[editingTagIndex] = currentTag;
      } else {
        updatedTags.push(currentTag);
      }

      setTags(updatedTags);
      setCurrentTag(null);
      setEditingTagIndex(null);
    }
  };

  const handleTagDelete = (index: number) => {
    setTags(tags.filter((_, i) => i !== index));

    if (!editorChanged) {
      dispatch(
        updateIGProductTagModalCloseState({
          state: true
        })
      );

      dispatch(
        setModalUiContent({
          modal: 'IGProductTags',
          attribute: 'allowClose',
          value: false
        })
      );

      setEditorChanged(true);
    }
  };

  const handleTagEdit = (e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    setEditingTagIndex(index);
    setCurrentTag(tags[index]);
  };

  useEffect(() => {
    setCurrentTag(null);
    setEditingTagIndex(null);
    setTags(instagramUserTags[selectedFile?.uuid] || []);
  }, [selectedFile, instagramUserTags]);

  const handleTagChange = (index: number, newText: string) => {
    setTags((prevTags) =>
      prevTags.map((tag, i) => (i === index ? { ...tag, text: newText } : tag))
    );
  };

  const handleSubmitTags = async () => {
    setStartSubmission(true);
    await dispatch(
      updateProductIGTags({ uuid: selectedFile?.uuid, newTags: tags })
    );
    dispatch(updateProductCatalog({ catalog: selectedCatalog }));

    dispatch(updateIGProductTagModalCloseState({ state: false }));
    dispatch(
      setModalUiContent({
        modal: 'IGProductTags',
        attribute: 'allowClose',
        value: true
      })
    );
    setEditorChanged(false);
    setTimeout(() => {
      setStartSubmission(false);
      lomavisShowToastr(
        t('lomavisCreator.additionalOptions.changesSaved'),
        'success'
      );
    }, 1000);
  };

  // useEffect(() => {
  //   if (imageRef.current) {
  //     setImageLoaded(false);
  //     const img = new Image();
  //     if (croppedImageUrl) {
  //       img.src = croppedImageUrl;
  //     } else {
  //       img.src = selectedFile?.preview_image;
  //     }
  //     img.onload = () => {
  //       setImageLoaded(true);
  //     };
  //   }
  // }, [selectedFile]);
  useEffect(() => {
    if (textRef.current) {
      textRef.current.focus();
    }
  }, [currentTag]);

  function updateHeight() {
    const elem = document.getElementById('container');
    const rect = elem.getBoundingClientRect();
    setContainerHeight(`${rect?.height}px`);
  }

  useEffect(() => {
    if (imageLoaded && imageRef.current) {
      // Small delay ensures image is fully laid out
      setTimeout(() => {
        const naturalWidth = imageRef.current?.naturalWidth;

        forceUpdate(); // This triggers re-render to recalculate tag positions
        setImageLoaded(false);
      }, 0);
    }
  }, [imageLoaded]);

  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  useEffect(() => {
    if (!instagram_shopping?.enabled) {
      lomavisShowToastr(
        t('lomavisCreator.additionalOptions.catalogFeatureNotEnabled'),
        'error'
      );
    } else if (!instagram_shopping?.catalog_url) {
      lomavisShowToastr(
        t('lomavisCreator.additionalOptions.catalogSearchNotAvailable'),
        'error'
      );
    }

    const handleResize = debounce(() => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    updateHeight(); // Initial call to set height

    const handleResize = () => {
      updateHeight();
    };

    window.addEventListener('resize', handleResize);

    // Cleanup event listener on component unmount
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <div
      onClick={() => {
        setCurrentTag(null);
        setEditingTagIndex(null);
      }}
      className="w-100 h-100 flex-fill d-flex justify-content-center align-items-center"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ overflow: 'auto' }}
        id="container"
        className="mx-auto align-items-center justify-content-center d-flex flex-column gap-4 w-100 h-100 "
      >
        {selectedFile?.media_type === IMAGE || VIDEO ? (
          <div className="d-flex gap-4 w-100 h-100">
            <div className="w-100 h-100 p-3 bg-white rounded-3 d-flex justify-content-center align-items-center">
              {crop && !croppedImageUrl ? (
                <div>
                  <Spinner animation="border" variant="primary" />
                </div>
              ) : (
                <div
                  className="position-relative d-flex flex-column gap-2 justify-content-center align-items-center p-3"
                  onClick={
                    instagram_shopping?.catalog_url &&
                    instagram_shopping?.enabled &&
                    !emptyCatalog &&
                    handleImageClick
                  }
                  ref={containerRef}
                  style={{ zIndex: 1 }}
                >
                  <img
                    ref={imageRef}
                    className="rounded"
                    src={croppedImageUrl || selectedFile?.preview_image}
                    alt="Selected"
                    style={{
                      maxWidth: '100%',
                      maxHeight: `calc(${containerHeight} - 30px)`,
                      objectFit: 'contain'
                    }}
                    onLoad={() => {
                      setTimeout(() => {
                        setImageLoaded(true);
                      }, 50);
                    }}
                  />

                  {tags.map((tag, index) => (
                    <div
                      key={index + 'tagss'}
                      className="position-absolute"
                      style={{
                        left: getRelativeX(tag.x_percentage),
                        top: getRelativeY(tag.y_percentage),
                        zIndex: 3
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="d-flex align-items-center justify-content-center rounded-circle border border-1 border-secondary text-white bg-dark p-1 px-0 w-30px h-30px fw-bold">
                        {index + 1}
                      </div>
                    </div>
                  ))}

                  {currentTag && (
                    <div
                      className="position-absolute"
                      style={{
                        left: getRelativeX(currentTag.x_percentage),
                        top: getRelativeY(currentTag.y_percentage),
                        zIndex: 4
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div
                        id="instagramProductTagBox"
                        className={`d-flex border border-1 ${
                          isFocused && currentTag.productInfo
                            ? 'border border-1'
                            : 'border-danger'
                        } align-items-center justify-content-center text-white bg-dark px-0 rounded-circle w-30px h-30px instagramProductTagBox`}
                        spellCheck={false}
                        contentEditable={false}
                        style={{ zIndex: 4 }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          id="collaboratorFieldText"
                          aria-placeholder={t(
                            'lomavisCreator.tagBox.placeholder'
                          )}
                          className="mx-auto fw-bold"
                          ref={tagRef}
                          onFocus={() => setIsFocused(true)}
                          onBlur={handleInputBlur}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              handleInputBlur();
                            }
                          }}
                        >
                          {tags.length + 1}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="h-100 w-75 bg-white rounded-3 p-2">
              {instagram_shopping?.catalog_url &&
              instagram_shopping?.enabled &&
              !emptyCatalog ? (
                <div className="h-100 w-100 d-flex flex-column gap-3">
                  <div className="p-2 d-flex flex-column border border-secondary rounded px-2">
                    <CreatorTitle
                      title="catalogSelect"
                      tooltipPosition={'right'}
                    />
                    <ProductCatalogSelection
                      selectedCatalog={selectedCatalog}
                      setSelectedCatalog={setSelectedCatalog}
                      setEmptyCatalog={setEmptyCatalog}
                      tags={tags}
                      instagramUserTags={instagramUserTags}
                    />
                  </div>
                  <div className="p-2 d-flex flex-column border border-secondary rounded px-2">
                    <CreatorTitle title="productTagInput" />
                    <TypeaheadProductSelection
                      textRef={textRef}
                      platform={INSTAGRAM}
                      name_key="instagram_shopping_tags"
                      currentTag={currentTag}
                      handleInputChange={handleInputChange}
                      handleInputBlur={handleInputBlur}
                      selectedCatalog={selectedCatalog}
                    />
                  </div>
                  {tags.length > 0 && (
                    <div className="p-2 d-flex flex-column border border-secondary rounded px-2 gap-2">
                      <div className="fw-bold text-dark user-select-none me-1">
                        {t(`lomavisCreator.title.taggedProduct`)}
                      </div>

                      <div className="d-flex flex-column gap-1">
                        {tags.map((item, index) => (
                          <div
                            key={item.productInfo?.product_name + index}
                            className="d-flex gap-2 align-items-center justify-content-center p-2 cursor-pointer"
                          >
                            <div>
                              <div className="d-flex align-items-center justify-content-center h-25px w-25px rounded-circle border border-1 border-secondary text-white bg-dark p-1 fw-bold">
                                {index + 1}
                              </div>
                            </div>
                            <ProductCard
                              imageUrl={item.productInfo?.image_url}
                              catalogName={selectedCatalog?.catalog_name}
                              productName={item.productInfo?.product_name}
                            />

                            <div className="d-flex justify-content-center align-items-center">
                              <i
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleTagDelete(index);
                                }}
                                className="fa-solid fa-xmark icon-sm text-muted ms-1 float-end pe-1 cursor-pointer closeBtn"
                              ></i>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="h-100 d-flex justify-content-center align-items-center text-center px-10 text-grey-900 fw-bold">
                  {!instagram_shopping?.enabled
                    ? t(
                        'lomavisCreator.additionalOptions.catalogFeatureNotEnabled'
                      )
                    : !instagram_shopping?.catalog_url
                    ? t(
                        'lomavisCreator.additionalOptions.catalogSearchNotAvailable'
                      )
                    : emptyCatalog
                    ? t('lomavisCreator.additionalOptions.emptyCatalog')
                    : t(
                        'lomavisCreator.additionalOptions.catalogSearchNotAvailable'
                      )}
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="position-relative d-flex flex-column gap-2 justify-content-center align-items-center p-3 bg-white rounded-3">
            {' '}
            <p className="h-500px w-500px bg-white d-flex justify-content-center align-items-center">
              {' '}
              <p className="text-dark">
                {t('lomavisCreator.IGtags.video_notAllowed')}
              </p>
            </p>{' '}
          </div>
        )}
      </div>

      <button
        onClick={handleSubmitTags}
        ref={submitRef}
        style={{ display: 'none' }}
      ></button>
    </div>
  );
};

export default Tagger;
